import { errorText } from './errorText';

/**
 * Where the error came from — the same browser error name means different
 * things for the microphone (phone timer) and Bluetooth (SG Timer).
 */
export type ErrorContext = 'ble' | 'mic' | 'general';

const NETWORK_TEXT =
  'Keine Verbindung zum Server. Bitte prüfe das Netzwerk und versuche es erneut.';

/** Our own raw error codes (thrown as `new Error(code)`) → German. */
const CODE_TEXT: Record<string, string> = {
  web_bluetooth_unavailable:
    'Web Bluetooth ist in diesem Browser nicht verfügbar (z. B. Safari/iOS). Bitte Chrome oder Edge verwenden.',
  not_connected: 'Der Timer ist nicht verbunden. Bitte zuerst koppeln.',
  gatt_missing: 'Die Bluetooth-Verbindung zum Timer konnte nicht aufgebaut werden.',
  connect_failed: 'Verbinden mit dem Timer fehlgeschlagen. Bitte erneut versuchen.',
  disconnect_failed: 'Trennen vom Timer fehlgeschlagen.',
  mic_unavailable: 'Das Mikrofon ist in diesem Browser nicht verfügbar.',
  no_audio_track: 'Vom Mikrofon kommt kein Signal.',
  mic_connect_failed: 'Das Mikrofon konnte nicht gestartet werden. Bitte erneut versuchen.',
  not_authenticated: 'Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.',
};

/** Browser (DOMException) error names → German, per context. */
function nameText(name: string, context: ErrorContext): string | null {
  if (context === 'mic') {
    switch (name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'Mikrofon-Zugriff wurde verweigert.';
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'Kein Mikrofon gefunden.';
      case 'NotReadableError':
      case 'AbortError':
        return 'Das Mikrofon ist belegt oder nicht lesbar (wird es von einer anderen App verwendet?).';
    }
    return null;
  }
  if (context === 'ble') {
    switch (name) {
      case 'NotFoundError':
        return 'Kein Timer ausgewählt — die Geräteauswahl wurde abgebrochen.';
      case 'NotAllowedError':
      case 'SecurityError':
        return 'Bluetooth-Zugriff wurde verweigert.';
      case 'NetworkError':
        return 'Die Bluetooth-Verbindung zum Timer ist fehlgeschlagen. Bitte erneut versuchen.';
      case 'NotSupportedError':
        return 'Dieser Timer wird nicht unterstützt.';
    }
  }
  return null;
}

/** English browser messages that can arrive as plain strings → German. */
function messageText(message: string, context: ErrorContext): string | null {
  if (
    /failed to fetch|networkerror when attempting|load failed|network request failed/i.test(message)
  )
    return NETWORK_TEXT;
  if (/requestDevice\(\) chooser|user cancel/i.test(message))
    return 'Kein Timer ausgewählt — die Geräteauswahl wurde abgebrochen.';
  if (
    context === 'ble' &&
    /bluetooth adapter not available|bluetooth is (turned )?off/i.test(message)
  )
    return 'Bluetooth ist ausgeschaltet oder nicht verfügbar.';
  if (context === 'mic' && /permission denied/i.test(message))
    return 'Mikrofon-Zugriff wurde verweigert.';
  return null;
}

/**
 * User-readable German text for a caught error or a stored error string.
 * Maps our raw codes (`not_connected`, `mic_connect_failed`, …), browser
 * DOMException names and common English browser messages; anything else
 * (e.g. an ApiError carrying the server's German text) goes through
 * `errorText`.
 */
export function userErrorText(err: unknown, context: ErrorContext = 'general'): string {
  if (typeof err === 'string') {
    return CODE_TEXT[err] ?? messageText(err, context) ?? err;
  }
  if (err instanceof Error) {
    const code = CODE_TEXT[err.message];
    if (code) return code;
    const byName = nameText(err.name, context);
    if (byName) return byName;
    const byMessage = messageText(err.message, context);
    if (byMessage) return byMessage;
  }
  return errorText(err);
}
